import type {
  Answer,
  AssistantError,
  AssistantMessagesPage,
  RunEvent,
} from '@agently/types';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { atom, map } from 'nanostores';

import { isAbortError, parseAssistantError } from './errors';
import { useGetThreadMessages } from './messages';
import { type AssistantPhase, reportRunFailure, reportStop } from './report';
import { streamRun } from './run';
import {
  forgetThread,
  useGetAssistantThread,
  useGetAssistantThreads,
} from './threads';

type RunStatus = 'idle' | 'running' | 'syncing' | 'error';

export interface RunState {
  status: RunStatus;
  /** A search is in flight and no text has started. */
  searching: boolean;
  /** The text block being streamed (final Answer or intermediate, not known until its END). */
  draft: string;
  /** The last finished block that turned out to be intermediate text. */
  intermediate: string | null;
  /** The finished Answer, kept until the reread history contains it. */
  answer: Answer | null;
  /** The optimistic member bubble. */
  question: string | null;
  error: AssistantError | null;
  /** Where the Run failed: before the stream opened, or after. */
  errorPhase: Exclude<AssistantPhase, 'sync'> | null;
  /** What "Try again" resends. */
  retryQuestion: string | null;
  controller: AbortController | null;
}

export const IDLE_RUN: RunState = {
  status: 'idle',
  searching: false,
  draft: '',
  intermediate: null,
  answer: null,
  question: null,
  error: null,
  errorPhase: null,
  retryQuestion: null,
  controller: null,
};

export const $runs = map<Record<string, RunState>>({});

/** Composer text per Thread; `NEW_THREAD_KEY` is the `/assistant` screen without a Thread. */
export const NEW_THREAD_KEY = 'new';
export const $composerDrafts = map<Record<string, string>>({});

/** Set once the server says the team's knowledge base is off; the Assistant turns read-only. */
export const $knowledgeDisabled = atom(false);

// Bumped on logout: a Run of the previous member must not write into the stores again.
let session = 0;

/**
 * Forgets everything the Assistant holds for the member (Runs, drafts, the
 * knowledge-disabled flag) and aborts their Runs. Call it on logout: signing in
 * again does not reload the page.
 */
export function resetAssistant() {
  session += 1;
  for (const run of Object.values($runs.get())) {
    run.controller?.abort();
  }
  openToolCalls.clear();
  $runs.set({});
  $composerDrafts.set({});
  $knowledgeDisabled.set(false);
}

export interface RunCallbacks {
  /** The Thread was deleted elsewhere (`THREAD_NOT_FOUND`). The Run may outlive its screen: check it is still open. */
  onThreadGone?: (threadId: string) => void;
}

export const getRun = (threadId: string): RunState =>
  $runs.get()[threadId] ?? IDLE_RUN;

const patch = (threadId: string, state: Partial<RunState>) =>
  $runs.setKey(threadId, { ...getRun(threadId), ...state });

export const setComposerDraft = (key: string, text: string) =>
  $composerDrafts.setKey(key, text);

const restoreComposer = (threadId: string, question: string) => {
  // Never overwrite what the member typed while the Run was going.
  if (!$composerDrafts.get()[threadId]) {
    setComposerDraft(threadId, question);
  }
};

type History = InfiniteData<AssistantMessagesPage, string | undefined>;

const readHistory = (client: QueryClient, threadId: string) =>
  client
    .getQueryData<History>(useGetThreadMessages.getKey({ threadId }))
    ?.pages.flatMap((page) => page.messages) ?? [];

// Fetches even when no screen observes the Thread; a failed reread is not fatal.
const rereadHistory = async (client: QueryClient, threadId: string) => {
  try {
    await client.fetchInfiniteQuery({
      ...useGetThreadMessages.getFetchOptions({ threadId }),
      staleTime: 0,
    });
  } catch (error) {
    // Keep what the cache has.
    reportRunFailure(parseAssistantError(error), 'sync');
  }
};

const openToolCalls = new Map<string, Set<string>>();

function reduce(threadId: string, event: RunEvent) {
  const run = getRun(threadId);
  const calls = openToolCalls.get(threadId) ?? new Set<string>();
  openToolCalls.set(threadId, calls);

  switch (event.type) {
    case 'TOOL_CALL_START':
      calls.add(event.toolCallId);
      patch(threadId, { searching: true });
      break;
    case 'TOOL_CALL_RESULT':
      calls.delete(event.toolCallId);
      patch(threadId, { searching: calls.size > 0 });
      break;
    case 'TEXT_MESSAGE_START':
      patch(threadId, { searching: false, draft: '', intermediate: null });
      break;
    case 'TEXT_MESSAGE_CONTENT':
      patch(threadId, { draft: run.draft + event.delta });
      break;
    case 'TEXT_MESSAGE_END':
      if (event.metadata) {
        patch(threadId, {
          draft: '',
          answer: {
            id: event.messageId,
            role: 'assistant',
            text: run.draft,
            createdAt: new Date().toISOString(),
            kind: event.metadata.kind,
            citations: event.metadata.citations ?? [],
            ...(event.metadata.uncovered
              ? { uncovered: event.metadata.uncovered }
              : {}),
          },
        });
      } else {
        patch(threadId, { draft: '', intermediate: run.draft });
      }
      break;
  }
}

interface Outcome {
  failure: AssistantError | null;
  finished: boolean;
  stopped: boolean;
}

async function consume(
  threadId: string,
  question: string,
  controller: AbortController,
): Promise<Outcome> {
  const outcome: Outcome = { failure: null, finished: false, stopped: false };

  try {
    for await (const event of streamRun({
      threadId,
      question,
      signal: controller.signal,
    })) {
      if (event.type === 'RUN_FINISHED') {
        outcome.finished = true;
      } else if (event.type === 'RUN_ERROR') {
        outcome.failure = {
          code: event.code,
          message: event.message,
          phase: 'stream',
        };
      } else {
        reduce(threadId, event);
      }
    }
  } catch (error) {
    if (isAbortError(error)) {
      outcome.stopped = true;
    } else {
      outcome.failure = parseAssistantError(error);
    }
  }
  openToolCalls.delete(threadId);

  return outcome;
}

// The server title and order follow the last message: refresh them in the background.
const refreshThreadMeta = (client: QueryClient, threadId: string) => {
  void client.invalidateQueries({
    queryKey: useGetAssistantThread.getKey({ threadId }),
  });
  void client.invalidateQueries({ queryKey: useGetAssistantThreads.getKey() });
};

async function settleFinished(
  threadId: string,
  question: string,
  client: QueryClient,
  current: () => boolean,
) {
  patch(threadId, { status: 'syncing', draft: '', searching: false });
  refreshThreadMeta(client, threadId);
  await rereadHistory(client, threadId);
  if (!current()) {
    return;
  }
  const { answer } = getRun(threadId);
  const synced =
    !answer || readHistory(client, threadId).some((m) => m.id === answer.id);
  // A failed reread keeps the finished local Answer on screen.
  $runs.setKey(threadId, synced ? IDLE_RUN : { ...IDLE_RUN, answer, question });
}

async function settleUnfinished(
  threadId: string,
  question: string,
  client: QueryClient,
  { failure, stopped }: Outcome,
  exchangeSaved: () => boolean,
  callbacks: RunCallbacks,
  current: () => boolean,
) {
  if (failure?.code === 'THREAD_NOT_FOUND') {
    // Deleted elsewhere: nothing to reread; carry the question to a new one.
    reportRunFailure(failure, failure.phase ?? 'pre-stream');
    forgetThread(client, threadId);
    $runs.setKey(threadId, IDLE_RUN);
    setComposerDraft(NEW_THREAD_KEY, question);
    callbacks.onThreadGone?.(threadId);
    return;
  }

  if (stopped) {
    // Gone at once; the reread below decides whether the exchange was saved after all.
    patch(threadId, {
      status: 'syncing',
      draft: '',
      intermediate: null,
      searching: false,
      answer: null,
      question: null,
    });
  }

  // Stop, RUN_ERROR and a drop: a late save may still have stored the exchange.
  await rereadHistory(client, threadId);
  if (!current()) {
    return;
  }
  // The failure happened even if the exchange turned out to be saved.
  if (failure) {
    reportRunFailure(failure, failure.phase ?? 'stream');
  }
  if (stopped) {
    reportStop();
  }
  if (exchangeSaved()) {
    refreshThreadMeta(client, threadId);
    $runs.setKey(threadId, IDLE_RUN);
    return;
  }
  restoreComposer(threadId, question);
  if (stopped) {
    $runs.setKey(threadId, IDLE_RUN);
    return;
  }
  if (failure?.code === 'KNOWLEDGE_DISABLED') {
    // The page explains it with the read-only state, not with an error.
    $knowledgeDisabled.set(true);
    $runs.setKey(threadId, IDLE_RUN);
    return;
  }

  const phase = failure?.phase ?? 'stream';
  $runs.setKey(threadId, {
    ...IDLE_RUN,
    status: 'error',
    error: failure,
    errorPhase: phase,
    retryQuestion: question,
    // Pre-stream errors remove the optimistic bubble.
    question: phase === 'stream' ? question : null,
  });
}

async function execute(
  threadId: string,
  question: string,
  client: QueryClient,
  controller: AbortController,
  callbacks: RunCallbacks,
) {
  const own = session;
  const current = () => own === session;
  // Without a cached history there is no baseline to tell a saved exchange from an older one.
  if (
    client.getQueryData(useGetThreadMessages.getKey({ threadId })) === undefined
  ) {
    await rereadHistory(client, threadId);
  }
  const knownIds = new Set(readHistory(client, threadId).map((m) => m.id));
  const exchangeSaved = () =>
    readHistory(client, threadId).some(
      (message) =>
        message.role === 'member' &&
        !knownIds.has(message.id) &&
        message.text.trim() === question.trim(),
    );

  const outcome = await consume(threadId, question, controller);
  if (!current()) {
    return;
  }
  if (outcome.finished) {
    await settleFinished(threadId, question, client, current);
  } else {
    await settleUnfinished(
      threadId,
      question,
      client,
      outcome,
      exchangeSaved,
      callbacks,
      current,
    );
  }
}

/** Starts a Run for a Thread. Plain function for event handlers; the Run outlives the screen. */
export function startRun(
  threadId: string,
  question: string,
  client: QueryClient,
  callbacks: RunCallbacks = {},
) {
  const { status } = getRun(threadId);
  if (status === 'running' || status === 'syncing') {
    return;
  }
  const controller = new AbortController();
  $runs.setKey(threadId, {
    ...IDLE_RUN,
    status: 'running',
    question,
    retryQuestion: question,
    controller,
  });
  setComposerDraft(threadId, '');
  void execute(threadId, question, client, controller, callbacks);
}

export function stopRun(threadId: string) {
  const { status, controller } = getRun(threadId);
  if (status === 'running') {
    controller?.abort();
  }
}

export function retryRun(
  threadId: string,
  client: QueryClient,
  callbacks?: RunCallbacks,
) {
  const { retryQuestion } = getRun(threadId);
  if (retryQuestion) {
    startRun(threadId, retryQuestion, client, callbacks);
  }
}
